import { StyleSheet, css } from 'aphrodite';
import { type FC, useMemo, useState } from 'react';

import { IconSearch, IconSparkles, IconX } from './icons';
import type { Conversation } from './seedConversations';

export type ConversationsPanelProps = {
	conversations: Conversation[];
	onSelect: (conversation: Conversation) => void;
};

type Bucket = { label: string; items: Conversation[] };

export const ConversationsPanel: FC<ConversationsPanelProps> = ({ conversations, onSelect }) => {
	const [query, setQuery] = useState('');

	const buckets = useMemo<Bucket[]>(() => {
		const q = query.trim().toLowerCase();
		const filtered = q
			? conversations.filter(
					c =>
						c.lead.toLowerCase().includes(q) ||
						c.contact.toLowerCase().includes(q) ||
						c.preview.toLowerCase().includes(q)
				)
			: conversations;

		const today = filtered.filter(c => /just now|^\d+[mh] ago|today/i.test(c.when));
		const earlier = filtered.filter(c => /yesterday|^(mon|tue|wed|thu|fri|sat|sun)$/i.test(c.when));
		const older = filtered.filter(c => /^[A-Z][a-z]{2} \d+/.test(c.when));

		return [
			{ label: 'Today', items: today },
			{ label: 'Earlier this week', items: earlier },
			{ label: 'Older', items: older },
		].filter(b => b.items.length > 0);
	}, [conversations, query]);

	return (
		<div className={css(styles.root)}>
			<div className={css(styles.header)}>
				<span className={css(styles.headerChip)}>
					<IconSparkles size={13} sw={2} color='var(--bg)' />
				</span>
				<div>
					<div className={css(styles.headerTitle)}>AI Conversations</div>
					<div className={css(styles.headerSubtitle)}>{conversations.length} across your pipeline · latest first</div>
				</div>
			</div>

			<div className={css(styles.searchWrap)}>
				<div className={css(styles.searchField)}>
					<IconSearch size={14} sw={1.8} color='var(--ink-faint)' />
					<input
						value={query}
						onChange={event => setQuery(event.target.value)}
						placeholder='Search conversations, leads, contacts…'
						className={css(styles.searchInput)}
					/>
					{query && (
						<button
							type='button'
							aria-label='Clear search'
							onClick={() => setQuery('')}
							className={css(styles.clearBtn)}
						>
							<IconX size={11} />
						</button>
					)}
				</div>
			</div>

			<div className={css(styles.list)}>
				{buckets.length === 0 && <div className={css(styles.empty)}>No conversations match “{query}”</div>}
				{buckets.map(bucket => (
					<div key={bucket.label} className={css(styles.bucket)}>
						<div className={css(styles.bucketLabel)}>{bucket.label}</div>
						<div className={css(styles.bucketItems)}>
							{bucket.items.map(c => (
								<button key={c.id} type='button' onClick={() => onSelect(c)} className={css(styles.row)}>
									<span className={css(styles.rowChip, c.live && styles.rowChipLive)}>
										<IconSparkles size={12} sw={2} />
									</span>
									<div className={css(styles.rowContent)}>
										<div className={css(styles.rowTop)}>
											<span className={css(styles.rowLead)}>{c.lead}</span>
											<span className={css(styles.rowDot)}>·</span>
											<span className={css(styles.rowContact)}>{c.contact}</span>
											{c.live && (
												<span className={css(styles.rowLive)}>
													<span className={css(styles.rowLiveDot)} />
													Live
												</span>
											)}
											<span className={css(styles.rowWhen)}>{c.when}</span>
										</div>
										<div className={css(styles.rowPreview)}>{c.preview}</div>
										<div className={css(styles.rowMeta)}>
											{c.messages} messages · {c.whenAbs}
										</div>
									</div>
								</button>
							))}
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

const styles = StyleSheet.create({
	root: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		background: 'var(--bg-elev)',
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
		padding: '14px 18px',
		borderBottom: '1px solid var(--border)',
		flexShrink: 0,
	},
	headerChip: {
		width: 28,
		height: 28,
		borderRadius: 8,
		background: 'var(--ink)',
		color: 'var(--bg)',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	headerTitle: {
		fontSize: 13,
		fontWeight: 600,
		color: 'var(--ink)',
	},
	headerSubtitle: {
		fontSize: 11,
		color: 'var(--ink-muted)',
	},
	searchWrap: {
		padding: '10px 14px',
		borderBottom: '1px solid var(--border)',
		flexShrink: 0,
	},
	searchField: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		padding: '7px 10px',
		background: 'var(--bg-sunken)',
		border: '1px solid var(--border)',
		borderRadius: 8,
	},
	searchInput: {
		flex: 1,
		border: 'none',
		outline: 'none',
		background: 'transparent',
		fontSize: 12.5,
		color: 'var(--ink)',
		fontFamily: 'inherit',
	},
	clearBtn: {
		width: 20,
		height: 20,
		border: 'none',
		background: 'transparent',
		color: 'var(--ink-muted)',
		cursor: 'pointer',
		borderRadius: 4,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	list: {
		flex: 1,
		overflowY: 'auto',
		padding: '4px 10px 14px',
	},
	empty: {
		padding: 32,
		textAlign: 'center',
		color: 'var(--ink-muted)',
		fontSize: 12.5,
	},
	bucket: {
		marginTop: 10,
	},
	bucketLabel: {
		fontSize: 11,
		fontWeight: 600,
		letterSpacing: '0.08em',
		textTransform: 'uppercase',
		color: 'var(--ink-faint)',
		padding: '6px 8px 4px',
	},
	bucketItems: {
		display: 'flex',
		flexDirection: 'column',
		gap: 2,
	},
	row: {
		textAlign: 'left',
		padding: 10,
		borderRadius: 8,
		background: 'transparent',
		border: 'none',
		display: 'flex',
		gap: 10,
		alignItems: 'flex-start',
		cursor: 'pointer',
		transition: 'background 120ms',
		fontFamily: 'inherit',
		':hover': {
			background: 'var(--hover)',
		},
	},
	rowChip: {
		width: 28,
		height: 28,
		borderRadius: 8,
		background: 'var(--bg-sunken)',
		color: 'var(--ink-muted)',
		border: '1px solid var(--border)',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		marginTop: 1,
	},
	rowChipLive: {
		background: 'var(--accent-soft)',
		color: 'var(--accent)',
	},
	rowContent: {
		flex: 1,
		minWidth: 0,
	},
	rowTop: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		marginBottom: 2,
	},
	rowLead: {
		fontSize: 12.5,
		fontWeight: 600,
		color: 'var(--ink)',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	rowDot: {
		color: 'var(--ink-faint)',
		fontSize: 11,
	},
	rowContact: {
		fontSize: 12,
		color: 'var(--ink-muted)',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	rowLive: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 3,
		marginLeft: 4,
		fontSize: 9,
		letterSpacing: '0.06em',
		color: 'var(--accent)',
		textTransform: 'uppercase',
		flexShrink: 0,
	},
	rowLiveDot: {
		width: 5,
		height: 5,
		borderRadius: 999,
		background: 'var(--accent)',
	},
	rowWhen: {
		marginLeft: 'auto',
		fontSize: 10.5,
		color: 'var(--ink-faint)',
		flexShrink: 0,
	},
	rowPreview: {
		fontSize: 12,
		color: 'var(--ink-muted)',
		lineHeight: 1.45,
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-expect-error — -webkit-box is a valid CSS value for line clamping
		display: '-webkit-box',
		WebkitLineClamp: 2,
		WebkitBoxOrient: 'vertical',
		overflow: 'hidden',
	},
	rowMeta: {
		marginTop: 4,
		fontSize: 10.5,
		color: 'var(--ink-faint)',
	},
});
